"use client";

import Link from "next/link";
import { useActionState, useState } from "react";
import { loginAction, type LoginFormState } from "@/lib/auth/actions";
import { useActionToast } from "@/components/ui/toast";

export function LoginForm({
  passwordResetOk,
  agreementRequired,
}: {
  passwordResetOk?: boolean;
  agreementRequired?: boolean;
}) {
  const [state, formAction, pending] = useActionState(loginAction, null as LoginFormState);
  useActionToast(state);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="relative mx-auto w-full min-w-0 max-w-[min(100%,25rem)] overflow-hidden rounded-[var(--radius-panel)] border border-[var(--border-strong)]/60 bg-[var(--surface-elevated)]/90 p-6 shadow-[var(--shadow-card)] backdrop-blur-sm sm:p-8">
      {/* Subtle top highlight */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(94,207,255,0.2)] to-transparent" aria-hidden />
      {passwordResetOk ? (
        <p
          className="mb-5 rounded-[0.5rem] border border-emerald-500/25 bg-emerald-500/[0.08] px-3.5 py-2.5 text-[13px] text-emerald-100/95"
          role="status"
        >
          Password updated. Sign in with your new password.
        </p>
      ) : null}
      {agreementRequired ? (
        <p
          className="mb-5 rounded-[0.5rem] border border-amber-500/25 bg-amber-500/[0.08] px-3.5 py-2.5 text-[13px] text-amber-100/95"
          role="status"
        >
          Sign your sales contractor agreement to access the platform. We&apos;ve emailed it to you
          from DocuSeal. Open that email and sign, then sign in here.
        </p>
      ) : null}
      <h1 className="text-lg font-semibold tracking-[-0.025em] text-[var(--foreground)]">Sign in</h1>
      <p className="mt-1.5 text-sm text-[var(--muted-foreground)]">Use your internal Ownly account.</p>
      <form action={formAction} className="mt-8 space-y-5">
        <div className="space-y-2">
          <label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--label)]">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            className="w-full rounded-[var(--radius-input)] border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 py-2.5 text-sm text-[var(--foreground)] shadow-[var(--shadow-inset-top),inset_0_-1px_0_rgba(0,0,0,0.15)] outline-none transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-[var(--label)] focus:border-[rgba(94,207,255,0.5)] focus:bg-[var(--surface-elevated)] focus:shadow-[var(--shadow-inset-top),0_0_0_3px_rgba(94,207,255,0.1),0_0_0_1px_rgba(94,207,255,0.35)]"
            placeholder="you@company.com"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--label)]">
            Password
          </label>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            className="w-full rounded-[var(--radius-input)] border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 py-2.5 text-sm text-[var(--foreground)] shadow-[var(--shadow-inset-top),inset_0_-1px_0_rgba(0,0,0,0.15)] outline-none transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-[var(--label)] focus:border-[rgba(94,207,255,0.5)] focus:bg-[var(--surface-elevated)] focus:shadow-[var(--shadow-inset-top),0_0_0_3px_rgba(94,207,255,0.1),0_0_0_1px_rgba(94,207,255,0.35)]"
          />
          <label className="flex cursor-pointer select-none items-center gap-2.5 pt-1 text-xs text-[var(--muted-foreground)]">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="size-3.5 shrink-0 rounded border border-[var(--border-strong)] bg-[var(--surface)] accent-[var(--accent)] shadow-[var(--shadow-inset-top)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-elevated)]"
            />
            Show password
          </label>
          <div className="pt-0.5 text-right">
            <Link
              href="/forgot-password"
              className="text-xs text-[var(--accent)]/90 underline-offset-2 hover:text-[var(--accent)] hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </div>
        {state?.error ? (
          <p className="text-sm text-red-300/90" role="alert">
            {state.error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={pending}
          className="flex w-full min-h-11 items-center justify-center rounded-[var(--radius-input)] bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--accent-foreground)] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(255,255,255,0.08),0_6px_20px_-8px_rgba(94,207,255,0.45)] transition-[filter,opacity,box-shadow] duration-150 hover:brightness-[1.08] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_8px_28px_-8px_rgba(94,207,255,0.55)] disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]"
        >
          {pending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
